import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, MessageSquare, Send } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function BlogPost() {
  const { id } = useParams();
  const { user } = useAuth();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState("");
  const [posting, setPosting] = useState(false);

  const load = () => api.get(`/blog/posts/${id}`).then((r) => setPost(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;
    setPosting(true);
    try {
      await api.post(`/blog/posts/${id}/comments`, { body: comment });
      setComment("");
      await load();
      toast.success("Comment posted!");
    } catch (err) { toast.error(apiErr(err)); }
    finally { setPosting(false); }
  };

  if (loading) return <div className="App bg-[#FFF4E6] min-h-screen"><Navbar /><p className="max-w-3xl mx-auto px-5 py-12 text-[#17324D]/80">Loading…</p></div>;
  if (!post) return <div className="App bg-[#FFF4E6] min-h-screen"><Navbar /><p className="max-w-3xl mx-auto px-5 py-12">Post not found. <Link to="/blog" className="text-[#D94A3D] underline">Back to blog</Link></p></div>;

  return (
    <div className="App bg-[#FFF4E6] min-h-screen">
      <Navbar />
      <article className="max-w-3xl mx-auto px-5 py-10">
        <Link to="/blog" data-testid="blog-back-link" className="inline-flex items-center gap-1 text-sm font-semibold text-[#17324D]/60 hover:text-[#D94A3D] mb-6"><ArrowLeft size={15} /> All posts</Link>
        <p className="text-xs tracking-[0.2em] uppercase text-[#D94A3D] font-semibold">{post.club_name}</p>
        <h1 className="font-heading font-extrabold text-3xl tracking-tight mt-2">{post.title}</h1>
        <p className="text-sm text-[#17324D]/60 mt-2">{post.published_at ? new Date(post.published_at).toLocaleDateString() : ""}</p>
        {post.image_url && <img src={`${BACKEND}${post.image_url}`} alt={post.title} className="w-full max-h-[420px] object-cover rounded-xl border border-[#17324D]/15 mt-5" />}
        <div className="mt-6 text-[#292524] text-lg leading-relaxed whitespace-pre-wrap">{post.body}</div>

        <div className="mt-12 border-t border-[#17324D]/15 pt-6">
          <h2 className="font-heading font-bold text-xl flex items-center gap-2"><MessageSquare size={24} className="text-[#17324D]" /> Comments ({post.comments?.length || 0})</h2>

          {user?.role === "customer" ? (
            <form onSubmit={submit} className="mt-4 flex gap-2" data-testid="comment-form">
              <input data-testid="comment-input" value={comment} onChange={(e) => setComment(e.target.value)}
                placeholder="Add a comment…" className="flex-1 border border-[#17324D]/15 rounded-xl px-3 py-2 bg-white focus:outline-none" />
              <button data-testid="comment-submit" disabled={posting}
                className="flex items-center gap-1 px-4 py-2 bg-[#17324D] text-white font-semibold border border-[#17324D]/15 rounded-xl hover:bg-[#0F2338] disabled:opacity-60">
                <Send size={15} /> Post
              </button>
            </form>
          ) : user ? (
            <p className="mt-4 text-sm text-[#17324D]/60" data-testid="comment-members-only">Only members can comment on posts.</p>
          ) : (
            <p className="mt-4 text-sm text-[#17324D]/60">
              <Link to="/login" className="text-[#D94A3D] underline">Log in</Link> as a member to leave a comment.
            </p>
          )}

          <div className="mt-6 space-y-4">
            {(post.comments || []).length === 0 ? (
              <p className="text-[#17324D]/60 text-sm">No comments yet. Be the first!</p>
            ) : post.comments.map((c) => (
              <div key={c.id} data-testid={`comment-${c.id}`} className="card-brut rounded-xl bg-white p-4">
                <div className="flex justify-between items-baseline">
                  <span className="font-semibold">{c.author_name}</span>
                  <span className="text-xs text-[#17324D]/50">{new Date(c.created_at).toLocaleString()}</span>
                </div>
                <p className="text-[#17324D]/80 mt-1">{c.body}</p>
              </div>
            ))}
          </div>
        </div>
      </article>
    </div>
  );
}
